// Modelled on packages/integrations/jira/frontend/src/install-steps.tsx, for
// the tracker's permissions and scopes, with P0 holding the credential.
import type { JiraTrackerSiteConfig } from "@p0security/integrations-jira-tracker-shared/types";
import { Steps, Typography } from "@p0security/ui";
import type { ComponentProps, ReactNode } from "react";

const { Text } = Typography;

/** The steps a site's install takes, in the order the customer takes them.
 * The site's host is entered when it's added, before these. */
const STEP_KEYS = [
  "service-account",
  "app-access",
  "permissions",
  "credential",
  "enter",
  "validate",
] as const;

type StepKey = (typeof STEP_KEYS)[number];

const STEP_TITLES: Readonly<Record<StepKey, string>> = {
  "service-account": "Create the service account",
  "app-access": "Give it access to Jira",
  permissions: "Give it the tracker's permissions",
  credential: "Create its OAuth credential",
  enter: "Enter the client ID and secret",
  validate: "Validate",
};

/** The steps the customer takes on the setup page, before Next. */
const SETUP_STEPS = 4;

/** The scopes the service account's OAuth credential needs: to read
 * projects and permissions, to create and edit issues, and to find people by
 * email. */
const OAUTH_SCOPES = [
  "read:jira-user",
  "read:jira-work",
  "write:jira-work",
] as const;

/** The project permissions the service account needs where P0 files
 * findings, as Jira's permission schemes name them. */
const PROJECT_PERMISSIONS = [
  "Browse projects",
  "Create issues",
  "Edit issues",
  "Assign issues",
] as const;

/** What each step says, for the site `host`. */
const toStepContent = (args: {
  host: string;
  stage: boolean;
}): Readonly<Record<StepKey, ReactNode>> => {
  const { host, stage } = args;
  return {
    "service-account": `In Atlassian Administration, create a service account for P0 in the organization that owns ${host}. P0 acts in Jira as this account.`,
    "app-access": `Give the service account access to Jira on ${host}.`,
    permissions: (
      <Text>
        In the permission scheme of the project P0 files findings in, give the
        service account {PROJECT_PERMISSIONS.join(", ")}. Then add it to a group
        that holds the global permission Browse users and groups, which P0 needs
        to find each issue&apos;s assignee by email. It needs nothing else: P0
        files and labels issues, and changes no Jira settings.
      </Text>
    ),
    credential: (
      <Text>
        Create an OAuth 2.0 credential for the service account, with the scopes{" "}
        {OAUTH_SCOPES.map((scope, index) => (
          <span key={scope}>
            {index > 0 ? ", " : ""}
            <Text code>{scope}</Text>
          </span>
        ))}
        . Copy its client ID and its client secret.
      </Text>
    ),
    enter: stage
      ? "Click Next. On the next page, enter the credential's client ID and client secret."
      : "Enter the credential's client ID and client secret below. P0 stores the client secret encrypted, and shows it to no one.",
    validate: `Click Finish. P0 checks that Atlassian issues a token for the credential, that ${host} answers, and that the service account holds the permissions above on at least one project. If a check fails, P0 shows what to fix at the top of this page. Then choose the project P0 files findings in, on the tracker's page.`,
  };
};

/**
 * A stored site's install steps.
 *
 * On the site's setup page (`stage`) the customer takes the first four, then
 * clicks Next. On the configure page they enter the client ID and secret and
 * click Finish, which validates the site.
 */
export const SiteSteps: React.FC<{
  /** The site's host, its item id */
  host: string;
  site: Pick<JiraTrackerSiteConfig, "state">;
}> = ({ host, site }) => {
  const stage = site.state === "stage";
  const done =
    site.state === "installed" ? STEP_KEYS.length : stage ? 0 : SETUP_STEPS;
  const current = stage ? SETUP_STEPS : STEP_KEYS.length;
  const content = toStepContent({ host, stage });
  const items: ComponentProps<typeof Steps>["items"] = STEP_KEYS.map(
    (key, index) => ({
      description: content[key],
      key,
      status: index < done ? "finish" : index < current ? "process" : "wait",
      title: STEP_TITLES[key],
    })
  );
  return (
    <Steps
      direction="vertical"
      items={items}
      size={stage ? undefined : "small"}
    />
  );
};
